import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fetchCategories } from "@/services/api";

// Shown until the live categories arrive (or if Firestore is unreachable).
const SHOP_FALLBACK = [
  { key: "books", to: "/shop/books", label: "Books" },
  { key: "collectibles", to: "/shop/collectibles", label: "Collectibles" },
];

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/founder", label: "Founder" },
  { to: "/featured-book", label: "Featured Book" },
  {
    label: "Shop",
    children: SHOP_FALLBACK,
  },
  { to: "/multimedia", label: "Multimedia" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { itemCount, openCart } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [shopLinks, setShopLinks] = useState(SHOP_FALLBACK);

  // The Shop menu mirrors whatever categories exist in the admin console.
  useEffect(() => {
    fetchCategories()
      .then((cats) => {
        if (cats.length) {
          setShopLinks(
            cats.map((c) => ({ key: c.slug, to: `/shop/${c.slug}`, label: c.name }))
          );
        }
      })
      .catch(() => {});
  }, []);

  const nav = useMemo(
    () => NAV.map((item) => (item.children ? { ...item, children: shopLinks } : item)),
    [shopLinks]
  );

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#6e5022]/15"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo.jpeg"
            alt="Ace Asia Productions logo"
            className="w-11 h-11 object-contain shrink-0"
          />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif-display text-xl text-[#281a12]">
              Ace Asia Productions
            </span>
            <span className="overline text-[#946f36]">Heritage · Stories · Craft</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setShopOpen(true)}
                onMouseLeave={() => setShopOpen(false)}
              >
                <button
                  data-testid={`nav-${item.label.toLowerCase()}-trigger`}
                  className="flex items-center gap-1 text-sm uppercase tracking-[0.18em] text-[#281a12] hover:text-[#6e5022] transition-colors"
                >
                  {item.label}
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                {shopOpen && (
                  <div className="absolute top-full left-0 pt-3 min-w-[220px]">
                    <div className="bg-[#FDFBF7] border border-[#6e5022]/20 shadow-sm py-2">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-shop-${c.key}-link`}
                          className="block px-5 py-2 text-sm whitespace-nowrap hover:bg-[#F4F1EA] text-[#281a12] hover:text-[#6e5022]"
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.18em] transition-colors ${
                    isActive
                      ? "text-[#6e5022] border-b border-[#6e5022] pb-1"
                      : "text-[#281a12] hover:text-[#6e5022]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className="hidden md:inline overline text-[#946f36] hover:text-[#6e5022]"
          >
            Admin
          </Link>
          <button
            data-testid="nav-cart-button"
            onClick={openCart}
            className="relative p-2 text-[#281a12] hover:text-[#6e5022] transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {itemCount > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1 -right-1 bg-[#6e5022] text-[#FDFBF7] text-[10px] font-medium w-5 h-5 flex items-center justify-center rounded-full"
              >
                {itemCount}
              </span>
            )}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#6e5022]/15 bg-[#FDFBF7]"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {nav.flatMap((item) =>
              item.children
                ? item.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${c.key}-link`}
                      className="py-2 text-sm uppercase tracking-[0.18em] text-[#281a12]"
                    >
                      Shop · {c.label}
                    </NavLink>
                  ))
                : [
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}-link`}
                      className="py-2 text-sm uppercase tracking-[0.18em] text-[#281a12]"
                    >
                      {item.label}
                    </NavLink>,
                  ],
            )}
            <Link
              to="/admin/login"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-nav-admin-link"
              className="py-2 text-sm uppercase tracking-[0.18em] text-[#946f36]"
            >
              Admin
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
